import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, type ArticleData } from '@/mocks/articles';

export default function ArticlePage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '').replace(/\/$/, '');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const article: ArticleData | undefined = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center bg-background-50 pt-24">
          <div className="text-center px-4">
            <h1 className="font-heading text-2xl font-bold text-foreground-950">Artikel nicht gefunden</h1>
            <p className="mt-3 text-foreground-600">Der gesuchte Artikel existiert leider nicht.</p>
            <Link to="/" className="inline-block mt-6 px-6 py-2.5 text-sm font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors cursor-pointer">
              Zurück zur Startseite
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: { '@type': 'Organization', name: 'OOPBuy Spreadsheet' },
    datePublished: article.datePublished,
    dateModified: article.datePublished,
    publisher: { '@type': 'Organization', name: 'OOPBuy Spreadsheet' },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Startseite', item: 'https://oopbuypread.com/' },
      { '@type': 'ListItem', position: 2, name: article.title, item: `https://oopbuypread.com/${article.slug}` },
    ],
  };

  return (
    <>
      <Navbar />

      <main>
        {/* Article Hero */}
        <section className="relative w-full min-h-[450px] md:min-h-[500px] flex items-center">
          <div className="absolute inset-0">
            <img
              src={article.heroImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/35"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 pt-24">
            <div className="max-w-4xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-background-100/70 mb-6">
                <Link to="/" className="hover:text-background-50 transition-colors cursor-pointer">
                  Startseite
                </Link>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </span>
                <span className="text-background-50">{article.title}</span>
              </nav>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-background-50 leading-tight tracking-tight">
                {article.title}
              </h1>
              <p className="mt-4 text-sm md:text-base text-background-100/80 max-w-2xl leading-relaxed">
                {article.subtitle}
              </p>

              <div className="mt-5 flex flex-wrap items-center gap-5 text-xs text-background-100/70">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line"></i>
                  </span>
                  {new Date(article.datePublished).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line"></i>
                  </span>
                  Lesezeit: {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-file-text-line"></i>
                  </span>
                  {article.wordCount}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-16 md:py-24 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            {/* Table of Contents */}
            <div className="bg-background-100 rounded-2xl p-6 md:p-8 mb-10">
              <h3 className="text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4">Inhaltsverzeichnis</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {article.sections.map((section, idx) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-s-line text-primary-500"></i>
                    </span>
                    {section.title}
                  </a>
                ))}
              </div>
            </div>

            {/* Sections */}
            {article.sections.map((section) => (
              <section key={section.id} id={section.id} className="mt-14 first:mt-0">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
                  {section.title}
                </h2>

                {section.content && section.content.map((paragraph, i) => (
                  <p key={i} className="mt-4 text-base text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}

                {section.table && (
                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b-2 border-background-300">
                          {section.table.headers.map((header, i) => (
                            <th key={i} className={`py-3 px-4 text-xs font-semibold text-foreground-800 ${i === 1 ? 'bg-primary-50/50 text-primary-600' : ''}`}>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.table.rows.map((row, ri) => (
                          <tr key={ri} className="border-b border-background-200/70">
                            {row.map((cell, ci) => (
                              <td key={ci} className={`py-3 px-4 text-sm ${ci === 0 ? 'font-medium text-foreground-800' : ci === 1 ? 'bg-primary-50/30 text-primary-600' : 'text-foreground-600'}`}>
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {section.tips && (
                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {section.tips.map((tip, i) => (
                      <div key={i} className="bg-background-100 rounded-xl p-5">
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-bold text-primary-600">{i + 1}</span>
                          </span>
                          <div>
                            <h3 className="text-sm font-semibold text-foreground-900">{tip.title}</h3>
                            <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed">{tip.detail}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {section.checklist && (
                  <div className="mt-6 bg-accent-50 rounded-xl p-6 border border-accent-200/60">
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center mt-0.5">
                        <i className="ri-shield-check-line text-accent-600 text-lg"></i>
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-900">Checkliste</h3>
                        <ul className="mt-2 space-y-1.5">
                          {section.checklist.map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-foreground-600">
                              <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
                                <i className="ri-check-line text-accent-500 text-xs"></i>
                              </span>
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            ))}

            {/* FAQs */}
            {article.faqs && article.faqs.length > 0 && (
              <section className="mt-14">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
                  Häufige Fragen und Antworten
                </h2>
                <div className="mt-6 space-y-4">
                  {article.faqs.map((faq, i) => (
                    <div key={i} className="bg-background-100 rounded-xl p-5">
                      <h3 className="text-sm font-semibold text-foreground-900">{faq.q}</h3>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Related Articles */}
            <section className="mt-14">
              <h2 className="font-heading text-xl font-bold text-foreground-950 tracking-tight mb-6">
                Weitere OOPBuy Spreadsheet Artikel
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {articles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 4)
                  .map((related) => (
                    <Link
                      key={related.slug}
                      to={`/${related.slug}`}
                      className="group bg-background-100 rounded-xl p-5 hover:bg-background-200/60 transition-colors cursor-pointer"
                    >
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug">
                        {related.title}
                      </h3>
                      <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed line-clamp-2">{related.subtitle}</p>
                      <span className="inline-flex items-center gap-1 mt-3 text-xs text-primary-500 font-medium">
                        Weiterlesen
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-arrow-right-line text-xs"></i>
                        </span>
                      </span>
                    </Link>
                  ))}
              </div>
            </section>

            {/* CTA am Ende */}
            <div className="mt-14 bg-primary-50 rounded-2xl p-8 text-center border border-primary-200/60">
              <h3 className="font-heading text-xl font-bold text-foreground-950">
                Bereit, das OOPBuy Spreadsheet selbst auszuprobieren?
              </h3>
              <p className="mt-3 text-sm text-foreground-600 max-w-lg mx-auto">
                Entdecke jetzt die komplette Produktvielfalt auf Kaspreadsheet und finde genau die Produkte, die du suchst.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="w-full sm:w-auto px-7 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  oopbuy spreadsheet
                  <i className="ri-arrow-right-up-line ml-2"></i>
                </a>
                <Link
                  to="/"
                  className="w-full sm:w-auto px-7 py-3 text-sm font-medium border-2 border-foreground-300 text-foreground-700 rounded-full hover:bg-foreground-950 hover:text-background-50 hover:border-foreground-950 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  Zurück zur Startseite
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
    </>
  );
}